<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { createEventDispatcher, onMount, onDestroy } from 'svelte';
  import * as apiService from '../lib/apiService';
  import { showToast } from '../lib/toast';
  import { openDialogWithSlideUp, closeDialogWithSlideDown } from '../lib/utils/dialogAnimations';
  
  export let sport = 'football';
  export let photoType = 'announcement'; // 'announcement' or 'match'
  export let matchId = null;

  const dispatch = createEventDispatcher();

  // State management
  let showStockPhotos = false;
  let userPhotos = [];
  let isLoadingUserPhotos = true;
  let showDeleteConfirm = false;
  let photoToDelete = null;
  let dialogElement: HTMLDialogElement;
  let confirmDialogElement: HTMLDialogElement;
  let confirmDialogContainer: HTMLDivElement;
  
  // Default images for each sport based on photo type
  const defaultImages = {
    announcement: {
      football: Array.from({ length: 12 }, (_, i) => `/img/backgrounds/announcement/football/background_${i+1}.jpg`),
      hockey: Array.from({ length: 12 }, (_, i) => `/img/backgrounds/announcement/hockey/background_${i+1}.jpg`)
    },
    match: {
      football: Array.from({ length: 12 }, (_, i) => `/img/backgrounds/match/football/background_${i+1}.jpg`),
      hockey: Array.from({ length: 12 }, (_, i) => `/img/backgrounds/match/hockey/background_${i+1}.jpg`)
    },
    award: {
      football: Array.from({ length: 12 }, (_, i) => `/img/backgrounds/award/football/background_${i+1}.jpg`),
      hockey: Array.from({ length: 12 }, (_, i) => `/img/backgrounds/award/hockey/background_${i+1}.jpg`)
    }
  };
  
  $: sportImages = defaultImages[photoType][sport.toLowerCase()] || defaultImages[photoType].football;
  
  // Load user photos on component mount
  onMount(async () => {
    // Trigger slide-up animation
    if (dialogElement) {
      await openDialogWithSlideUp(dialogElement);
    }
    await loadUserPhotos();

    // Create a container for the confirmation dialog at body level
    confirmDialogContainer = document.createElement('div');
    confirmDialogContainer.id = 'photo-delete-confirm-portal';
    document.body.appendChild(confirmDialogContainer);
  });

  onDestroy(() => {
    // Clean up the portal container
    if (confirmDialogContainer && confirmDialogContainer.parentNode) {
      confirmDialogContainer.parentNode.removeChild(confirmDialogContainer);
    }
  });
  
  async function loadUserPhotos() {
    isLoadingUserPhotos = true;
    try {
      await apiService.getUserPhotos((result, status) => {
        if (status === 200) {
          userPhotos = result.photos;
        } else {
          console.error('Failed to load user photos');
          userPhotos = [];
        }
        isLoadingUserPhotos = false;
      });
    } catch (error) {
      console.error('Error loading user photos:', error);
      userPhotos = [];
      isLoadingUserPhotos = false;
    }
  }
  
  // Handle file upload
  async function handleFileSelect(event) {
    console.log('handleFileSelect called');
    const file = event.target.files[0];
    if (file) {
      // Check if user has reached the 10 image limit
      if (userPhotos.length >= 10) {
        showToast($_('photoSelectionDialog.maxImagesReached'), 'error');
        event.target.value = ''; // Clear the file input
        return;
      }
      
      // Check file size (10MB = 10 * 1024 * 1024 bytes)
      const maxSize = 15 * 1024 * 1024;
      if (file.size > maxSize) {
        showToast($_('match.preparation.fileTooLarge'), 'error');
        event.target.value = ''; // Clear the file input
        return;
      }

      try {
        // Show loading state while uploading
        isLoadingUserPhotos = true;
        await apiService.uploadMatchPhoto(matchId.toString(), photoType, file, (result, status) => {
          isLoadingUserPhotos = false;
          if (status === 200) {
            showToast('Image uploaded successfully', 'success');
            
            // Refresh user photos list to include the new upload
            loadUserPhotos();
            
            // Dispatch the photo selection event
            dispatch('select', {
              file: file,
              filename: result.filename,
              url: result.url,
              type: 'custom'
            });

            apiService.trackEvent('upload_photo', {
              module: "utils",
              action_type: "upload_media",
              description: 'Upload a photo',
              value: photoType
            });
          } else {
            showToast('Failed to upload image', 'error');
          }
        });
      } catch (error) {
        isLoadingUserPhotos = false;
        console.error('Error in file upload:', error);
        showToast('Failed to upload image: ' + error.message, 'error');
      }
    }
  }
  
  // Select user uploaded image
  async function selectUserPhoto(photo) {
    dispatch('select', {
      file: null,
      filename: photo.key_path,
      url: photo.url,
      type: 'user'
    });
    await closeDialog();
  }

  // Select default image
  async function selectDefaultImage(imagePath) {
    dispatch('select', {
      file: null,
      filename: imagePath.split('/').pop(),
      url: imagePath,
      type: 'default'
    });
    await closeDialog();
  }

  async function closeDialog() {
    await closeDialogWithSlideDown(dialogElement);
    dispatch('close');
  }
  
  // Delete photo functions
  function showDeleteDialog(photo) {
    photoToDelete = photo;
    showDeleteConfirm = true;

    // Manually render the dialog in the portal container
    setTimeout(() => {
      if (confirmDialogElement && confirmDialogContainer) {
        confirmDialogContainer.appendChild(confirmDialogElement);
        confirmDialogElement.showModal();
      }
    }, 10);
  }
  
  function closeDeleteDialog() {
    if (confirmDialogElement) {
      confirmDialogElement.close();
    }
    showDeleteConfirm = false;
    photoToDelete = null;
  }
  
  async function confirmDeletePhoto() {
    if (!photoToDelete) return;
    
    try {
      // Call API to delete the photo
      await apiService.deleteUserPhoto(photoToDelete.filename, (result, status) => {
        if (status === 200) {
          // Refresh the photos list
          loadUserPhotos();
          apiService.trackEvent('delete_photo', {
            module: "utils",
            action_type: "delete_media",
            description: 'Delete a photo',
            value: photoToDelete.filename,
          });
        } else {
          showToast($_('photoSelectionDialog.deletePhotoFailed'), 'error');
        }
        closeDeleteDialog();
      });
    } catch (error) {
      console.error('Error deleting photo:', error);
      showToast($_('photoSelectionDialog.deletePhotoFailed'), 'error');
      closeDeleteDialog();
    }
  }
</script>

<dialog bind:this={dialogElement} class="slide-up-dialog">
  <article class="mt-0">
    <header class="py-4">
      <h5>{$_('photoSelectionDialog.chooseImage')}</h5>
      <button class="close-button" on:click|stopPropagation={closeDialog}>×</button>
    </header>

    <!-- Toggle between user photos and stock photos -->
    {#if !showStockPhotos}
      <!-- User Photos Section -->
      <div class="section-header">
        <h6>{$_('photoSelectionDialog.yourUploadedPhotos')}</h6>
        {#if !isLoadingUserPhotos}
          <div class="photo-count">
            {userPhotos.length}/15 {$_('photoSelectionDialog.imagesUploaded')}
            {#if userPhotos.length >= 15}
              <span class="limit-reached">({$_('photoSelectionDialog.limitReached')})</span>
            {/if}
          </div>
        {/if}
        {#if !isLoadingUserPhotos && userPhotos.length === 0}
          <p class="empty-message">{$_('photoSelectionDialog.noPhotosUploaded')}</p>
        {/if}
      </div>

      {#if isLoadingUserPhotos}
      <article aria-busy="true">
        <p>{$_('photoSelectionDialog.loadingPhotos')}</p>
      </article>
      {:else if userPhotos.length > 0}
        <div class="image-gallery">
          {#each userPhotos as photo}
            <div class="image-item" role="button" tabindex="0"
                 on:click={() => selectUserPhoto(photo)}
                 on:keydown={(e) => e.key === 'Enter' && selectUserPhoto(photo)}>
              <img src={photo.url} alt={photo.filename} />
              <button
                class="delete-photo-btn"
                on:click={(e) => {
                  console.log('Delete button clicked!');
                  e.preventDefault();
                  e.stopPropagation();
                  showDeleteDialog(photo);
                }}
                aria-label={$_('photoSelectionDialog.deletePhoto')}
                title={$_('photoSelectionDialog.deletePhoto')}>
                ×
              </button>
            </div>
          {/each}
        </div>
      {/if}
    {:else}
      <!-- Stock Photos Section -->
      <div class="section-header">
        <h6>{$_('photoSelectionDialog.stockPhotos')}</h6>
      </div>

      <div class="image-gallery">
        {#each sportImages as imagePath, index}
          <div class="image-item" role="button" tabindex="0"
               on:click={() => selectDefaultImage(imagePath)}
               on:keydown={(e) => e.key === 'Enter' && selectDefaultImage(imagePath)}>
            <img src={imagePath} alt={`Background ${index + 1}`} />
          </div>
        {/each}
      </div>
    {/if}

    <footer>
      {#if !showStockPhotos}
      <button
        class=" full-width"
        disabled={userPhotos.length >= 10}
        on:click={() => document.getElementById(`${photoType}-photo-upload`).click()}>
        <ion-icon name="cloud-upload-outline"></ion-icon>
        {userPhotos.length >= 10 ? $_('photoSelectionDialog.uploadLimitReached') : $_('photoSelectionDialog.uploadImage')}
      </button>
      {/if}
      <button class="outline full-width ml-0 mt-6" on:click={() => showStockPhotos = !showStockPhotos}>
        <ion-icon name="{showStockPhotos ? 'arrow-back-outline' : 'images-outline'}"></ion-icon>
        {showStockPhotos ? $_('photoSelectionDialog.yourPhotos') : $_('photoSelectionDialog.useStockImage')}
      </button>
      <input
        id="{photoType}-photo-upload"
        type="file"
        accept="image/*"
        on:change={handleFileSelect}
        style="display: none;"
      />
    </footer>
  </article>
</dialog>

<!-- Delete confirmation dialog - rendered to body via portal -->
{#if showDeleteConfirm && photoToDelete}
<dialog bind:this={confirmDialogElement} class="confirmation-dialog">
  <article>
    <header>
      <h6>{$_('photoSelectionDialog.deletePhotoTitle')}</h6>
    </header>
    <p>{$_('photoSelectionDialog.deletePhotoConfirm')}</p>
    <footer>
      <div class="button-row">
        <button class="secondary" on:click={closeDeleteDialog}>
          {$_('common.cancel')}
        </button>
        <button class="primary" on:click={confirmDeletePhoto}>
          {$_('common.delete')}
        </button>
      </div>
    </footer>
  </article>
</dialog>
{/if}

<style>
  /* Section styling */
  .section-header {
    margin: 1rem 0 0.5rem 0;
  }
  
  .section-header h6 {
    margin: 0 0 0.5rem 0;
    color: var(--pico-color);
  }
  
  .photo-count {
    font-size: 0.875rem;
    color: var(--pico-muted-color);
    margin-bottom: 0.5rem;
  }
  
  .limit-reached {
    color: var(--pico-del-color);
    font-weight: 500;
  }
  
  .empty-message {
    color: var(--pico-muted-color);
    text-align: center;
    margin: 2rem 0;
    font-style: italic;
  }
  
  .loading-state {
    text-align: center;
    margin: 2rem 0;
    color: var(--pico-muted-color);
  }

  /* Gallery styling */
  .image-gallery {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 0rem;
    margin: 1rem 0;
  }

  .image-item {
    cursor: pointer;
    border-radius: var(--pico-border-radius);
    overflow: hidden;
    transition: transform 0.2s;
    position: relative;
    border: none;
    background: transparent;
  }

  .image-item:hover {
    transform: scale(1.05);
  }
  
  .image-item:focus {
    outline: none;
    border: none;
    transform: scale(1.05);
  }

  .image-item img {
    width: 100%;
    height: 120px;
    object-fit: cover;
  }
  
  .delete-photo-btn {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.8);
    border: none;
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: bold;
    line-height: 1;
    transition: all 0.2s;
    z-index: 10;
  }
  
  .delete-photo-btn:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }
  
  .delete-photo-btn:focus {
    outline: 2px solid white;
    outline-offset: 2px;
  }
  
  .image-label {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  /* Footer button layout */
  .button-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }
  
  .button-row button {
    margin: 0;
  }
  
  /* Confirmation dialog */
  :global(dialog.confirmation-dialog) {
    z-index: 10000 !important;
    position: fixed !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    margin: 0 !important;
    max-width: 400px !important;
    width: 90% !important;
  }

  :global(dialog.confirmation-dialog::backdrop) {
    background: rgba(0, 0, 0, 0.7) !important;
    z-index: 9999 !important;
  }

  .confirmation-dialog article {
    max-width: 400px;
    margin: auto;
  }
</style> 